'use client';

import { hardNavigateWithCampaignParams } from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { loginInfoQueryCreator } from '@/query/loginInfoQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { FullPageLoading } from '@/shared/components/loading';
import type { InvitationDecodeType } from '@/shared/types/auth';
import { useDecodeInvitation } from '../hooks/useDecodeInvitation';

/**
 * InviteMainPage handles the /invite/:token route.
 * User comes to this page from an workspace-invitation email.
 *
 * Flow:
 * 0. If no token → redirect to /home (logged in) or /signin (not logged in)
 * 1. Decode the invitation token
 *    - If decode fails → redirect to /home (logged in) or /signup/:token (not logged in)
 * 2. If user IS logged in:
 *    - If email matches current user → redirect to /home
 *    - If email doesn't match:
 *      - If valid workspace with SSO enabled → redirect to /sso-login
 *      - If valid workspace without SSO → redirect to /join-team-warning
 *      - If no valid workspace → redirect to /home
 * 3. If user is NOT logged in:
 *    - If valid workspace with SSO enabled → redirect to /sso-login
 *    - If new user (user_email === false) → redirect to /signup/:token
 *    - If existing user → redirect to /signin/:token
 */

export default function InviteMainPage() {
  const pathname = usePathname();
  const token = pathname.split('/').pop();

  const { data: loginInfo, isLoading: isLoadingLoginInfo } = useQuery({
    ...loginInfoQueryCreator(),
    retry: false,
  });

  const isLoggedIn = loginInfo?.['logged-in'] ?? false;

  const { data: user, isLoading: isLoadingUser } = useQuery(
    userProfileQueryCreator({ enabled: isLoggedIn }),
  );

  const currentUserEmail = user?.email ?? '';

  const { decodeInvitation } = useDecodeInvitation({
    onSuccess: (invitation: InvitationDecodeType) => {
      const inviteEmail = (invitation.email ?? '').toLowerCase();
      const workspace = invitation.workspace;
      const isValidWorkspace = workspace && Object.keys(workspace).length > 0;
      const isSSOEnabled = workspace.sso_enabled === true;

      if (isLoggedIn) {
        if (inviteEmail === currentUserEmail) {
          hardNavigateWithCampaignParams(`/${PATH_URL.HOME}`);
        } else if (isValidWorkspace) {
          if (isSSOEnabled) {
            hardNavigateWithCampaignParams(`/${PATH_URL.SSO_LOGIN}`);
          } else {
            hardNavigateWithCampaignParams(`/${PATH_URL.JOIN_TEAM_WARNING}`);
          }
        } else {
          hardNavigateWithCampaignParams(`/${PATH_URL.HOME}`);
        }
      } else {
        if (isValidWorkspace && isSSOEnabled) {
          hardNavigateWithCampaignParams(`/${PATH_URL.SSO_LOGIN}`);
        } else {
          const isNewUser = invitation.user_email === false;

          hardNavigateWithCampaignParams(
            isNewUser
              ? `/${PATH_URL.SIGNUP}/${token}`
              : `/${PATH_URL.SIGNIN}/${token}`,
          );
        }
      }
    },
    onError: () => {
      if (!isLoggedIn) {
        hardNavigateWithCampaignParams(`/${PATH_URL.SIGNUP}/${token}`);
      } else {
        hardNavigateWithCampaignParams(`/${PATH_URL.HOME}`);
      }
    },
  });

  useEffect(() => {
    if (isLoadingLoginInfo) return;

    if (!token) {
      hardNavigateWithCampaignParams(
        `/${isLoggedIn ? PATH_URL.HOME : PATH_URL.SIGNIN}`,
      );
      return;
    }

    if (isLoadingUser) return;

    decodeInvitation(token);
  }, [isLoadingLoginInfo, isLoggedIn, token, decodeInvitation, isLoadingUser]);

  return <FullPageLoading />;
}
